'use strict';
// Shared by browser metadata and the offline renderer. No provider or DOM access.
const normalizePath = value => String(value || '/').split(/[?#]/)[0].replace(/\/+$/, '') || '/';
const PRIVATE = /^(?:\/(?:admin|customer|commerce|cart|checkout|agreement|downloads)(?:\/|$)|\/email-preferences(?:\/|$)|\/careers\/assessment(?:\/|$)|\/work-with-us\/(?:apply|application-received)(?:\/|$)|\/companies\/(?:application-submitted)(?:\/|$)|\/paperwork\/[^/]+\/(?:checkout|apply)(?:\/|$))/;
const isPrivatePath = value => PRIVATE.test(normalizePath(value));
const isPaperworkAlias = value => /^\/paperwork\/(?:basic|standard|premium|packages\/[^/]+)$/.test(normalizePath(value));
function unavailable(path, pending = false) {
  return { path: normalizePath(path), type: 'noindex', noindex: true, sitemap: false,
    title: pending ? 'Magellan' : 'Content unavailable | Magellan', description: '', h1: 'Content unavailable', omitCanonical: true };
}
function applyPolicy(route) {
  const path = normalizePath(route.path);
  if (isPrivatePath(path)) return { ...unavailable(path), title: path.startsWith('/admin') ? 'Magellan Admin' : path.startsWith('/customer/access') ? 'Customer Access | Magellan' : 'Magellan' };
  if (path === '/contact' || path === '/book-a-meeting') return { ...route, path, canonicalPath: '/', noindex: true, sitemap: false, prerender: false };
  if (isPaperworkAlias(path)) return { ...route, path, canonicalPath: '/paperwork', sitemap: false, prerender: false };
  const serviceAliases = { '/services/tms-operations': '/services/tms-platform', '/services/safety-systems': '/services/safety-support', '/services/premium-freight-paperwork': '/services/specialized-freight-readiness' };
  if (serviceAliases[path]) return { ...route, path, canonicalPath: serviceAliases[path], sitemap: false, prerender: false };
  if (path === '/tms') return { ...route, path, canonicalPath: '/tms', sitemap: true };
  return { ...route, path, ...(route.noindex ? { omitCanonical: true, sitemap: false } : {}) };
}
function canonicalUrl(route, config) {
  if (!route || route.omitCanonical || isPrivatePath(route.path)) return null;
  const path = normalizePath(route.canonicalPath || route.path);
  // Only safe same-site paths; never reflect a query, fragment, token or origin.
  if (!/^\/(?:[a-z0-9-]+(?:\/[a-z0-9-]+)*)?$/.test(path) || isPrivatePath(path)) return null;
  return config.siteUrl + path;
}
function imageUrl(value, config) {
  try {
    const url = new URL(value || config.defaultImage, config.siteUrl);
    if (url.protocol === 'https:' && !url.username && !url.password) return url.href;
  } catch { /* Use the existing approved default. */ }
  return config.defaultImage;
}
function findRoute(pathname, routes) {
  const path = normalizePath(pathname);
  if (isPrivatePath(path)) return applyPolicy({ path });
  const route = routes.find(r => r.path === path)
    || (isPaperworkAlias(path) ? { ...routes.find(r => r.path === '/paperwork'), path } : null);
  return route ? applyPolicy(route) : unavailable(path);
}
function sitemapRoutes(routes, config) {
  const seen = new Set();
  return routes.map(applyPolicy).filter(route => {
    const url = canonicalUrl(route, config);
    if (route.noindex || route.sitemap === false || !url || normalizePath(route.canonicalPath || route.path) !== route.path || seen.has(url)) return false;
    seen.add(url); return true;
  });
}
function buildSchema(route, config) {
  const url = canonicalUrl(route, config);
  if (!url || route.noindex) return null;
  const org = `${config.siteUrl}/#organization`;
  const graph = [
    { '@type': 'Organization', '@id': org, name: config.siteName, url: config.siteUrl, email: 'support@magellan-tms.com', telephone: '+14637772573' },
    { '@type': 'WebSite', '@id': `${config.siteUrl}/#website`, name: config.siteName, url: config.siteUrl, publisher: { '@id': org } },
    { '@type': 'BreadcrumbList', itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: config.siteUrl },
      ...(route.path === '/' ? [] : [{ '@type': 'ListItem', position: 2, name: route.breadcrumbLabel || route.h1 || route.title, item: url }]),
    ] },
  ];
  if (['service', 'serviceOverview'].includes(route.type)) graph.push({ '@type': 'Service', name: route.h1, description: route.description, provider: { '@id': org }, areaServed: 'United States' });
  // Catalogue copy is not evidence of a dated scheduled event.
  if (route.type === 'course') graph.push({ '@type': 'Course', name: route.h1, description: route.description, provider: { '@id': org } });
  if (route.type === 'article') graph.push({ '@type': 'Article', headline: route.h1, description: route.description,
    publisher: { '@id': org }, mainEntityOfPage: url, image: imageUrl(route.image, config),
    ...(route.publishedAt ? { datePublished: route.publishedAt } : {}), ...(route.updatedAt ? { dateModified: route.updatedAt } : {}) });
  // FAQs require page-specific visible-copy approval; do not infer them from legacy SEO snapshots.
  return { '@context': 'https://schema.org', '@graph': graph };
}
module.exports = { normalizePath, isPrivatePath, isPaperworkAlias, unavailable, applyPolicy, canonicalUrl, imageUrl, findRoute, sitemapRoutes, buildSchema };
